import * as R from 'ramda'

import contentfulClient from 'clients/contentful'
import apolloClient from 'clients/apollo'

import {
  dynamicMetaTagsConverter,
  generateCMSDynamicLocalText,
} from 'components/city-page/helpers'
import CityPage from 'components/city-page/index'
import Layout from 'components/layout'

import ErrorPage from 'pages/_error'

import {
  getCityBySlug,
  getCountryCitySlugs,
  getS3Content,
} from './queries.graphql'
import { useIsMobileLayout } from 'components/dashboard/clients/hooks'
import { HeeboProvider } from 'providers/font-providers/heebo-provider'

export const SURVEY_ID = 3

export enum LOCAL_PAGE_TYPE {
  CITY = 'City',
  STATE = 'State',
  COUNTRY = 'Country',
}

const LocalPage = ({
  statusCode,
  locationDetails,
  fields,
  survey,
  localPageType,
}) => {
  const isMobile = useIsMobileLayout()

  if (statusCode !== 200) {
    return <ErrorPage statusCode={statusCode} title="Error" />
  }

  const {
    title: dynamicTitle,
    metaTags: dynamicMetaTags,
    noIndex,
    canonicalUrl: dynamicCanonicalUrl,
  } = fields

  const metaTags = dynamicMetaTagsConverter(dynamicMetaTags, locationDetails)
  const title = generateCMSDynamicLocalText(dynamicTitle, locationDetails)
  const canonicalUrl = generateCMSDynamicLocalText(
    dynamicCanonicalUrl,
    locationDetails
  )

  return (
    <>
      <HeeboProvider>
        <Layout
          title={title}
          metaTags={metaTags}
          noIndex={noIndex}
          canonicalUrl={canonicalUrl}
          includeChat={true}>
          <CityPage
            fields={fields}
            isMobile={isMobile}
            survey={survey}
            locationDetails={locationDetails}
            localPageType={localPageType}
            apolloClient={undefined}
            apolloState={undefined}
          />
        </Layout>
      </HeeboProvider>

      <style
        // @ts-ignore
        jsx
        global>{`
        body {
          overflow-x: hidden;
        }

        body #crisp-chatbox > div > a > span {
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.2) !important;
        }
      `}</style>
    </>
  )
}

async function cityStaticProps(env, citySlug) {
  let overrideDefault, config, surveyPayload, locationPayload

  // Loads local page content from our S3 cache in staging/production
  // to avoid hitting Contentful API rate limits while building
  if (env === 'development') {
    const localPageContentPromise = contentfulClient.getEntries({
      content_type: 'localPageCity',
      'fields.localPageCity[in]': `DEFAULT,${citySlug}`,
      order: 'sys.createdAt',
      include: 10,
    })
    const surveyContentPromise = contentfulClient.getEntries({
      content_type: 'survey',
      'fields.id[in]': SURVEY_ID,
      include: 10,
    })

    const [location, contentPayload, survey] = await Promise.all([
      apolloClient.query({
        query: getCityBySlug,
        variables: { citySlug },
      }),
      localPageContentPromise,
      surveyContentPromise,
    ])

    // Allow overriding default content if found a Contentful city specific page
    overrideDefault = contentPayload?.items[1]
    config = overrideDefault || R.head(contentPayload.items)

    surveyPayload = survey
    locationPayload = location
  } else {
    const localPageContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: `localPageCity/${citySlug}.json` },
    })
    const defaultLocalPageContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: 'localPageCity/DEFAULT.json' },
    })
    const surveyContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: 'survey/3.json' },
    })

    const [location, defaultContentPayload, contentPayload, survey] =
      await Promise.all([
        apolloClient.query({
          query: getCityBySlug,
          variables: { citySlug },
        }),
        defaultLocalPageContentPromise,
        localPageContentPromise,
        surveyContentPromise,
      ])

    // Allow overriding default content if found a Contentful city specific page
    overrideDefault = R.head(
      R.pathOr([], ['data', 's3Content', 'items'], contentPayload)
    )
    config =
      overrideDefault || R.head(defaultContentPayload.data.s3Content.items)

    surveyPayload = survey.data.s3Content
    locationPayload = location
  }

  const locationDetails = overrideDefault
    ? {
        city: {
          name: overrideDefault.fields.cityName,
          slug: citySlug,
          state: {
            name: overrideDefault.fields.stateName,
            abbreviation: overrideDefault.fields.stateAbbreviation,
            slug: overrideDefault.fields.stateSlug,
            country: {
              abbreviation: overrideDefault.fields.countryAbbreviation,
              slug: overrideDefault.fields.countrySlug,
            },
          },
        },
      }
    : locationPayload.data.localPageCityLocation

  const minCityCoachesThreshold = 5

  if (
    locationDetails &&
    R.length(
      R.pathOr(
        [],
        ['data', 'localPageCityLocation', 'cityCoaches'],
        locationPayload
      )
    ) < minCityCoachesThreshold
  ) {
    // Redirect to state page if city exists but has less than 5 coaches
    const stateRedirect = `${R.pathOr(
      [],
      ['city', 'state', 'country', 'slug'],
      locationDetails
    )}/${R.pathOr([], ['city', 'state', 'slug'], locationDetails)}`

    return {
      stateRedirect,
      locationDetails,
    }
  }

  return { config, surveyPayload, locationDetails, stateRedirect: null }
}

async function countryStaticProps(env, countrySlug) {
  let config, surveyPayload, locationPayload

  // Loads local page content from our S3 cache in staging/production
  // to avoid hitting Contentful API rate limits while building
  if (env === 'development') {
    const localPageContentPromise = contentfulClient.getEntries({
      content_type: 'localPageCountry',
      'fields.localPageCountry[in]': countrySlug,
      limit: 1,
      include: 10,
    })
    const surveyContentPromise = contentfulClient.getEntries({
      content_type: 'survey',
      'fields.id[in]': SURVEY_ID,
      include: 10,
    })

    const [contentPayload, survey] = await Promise.all([
      localPageContentPromise,
      surveyContentPromise,
    ])

    locationPayload = R.head(contentPayload.items) || null
    config = R.head(contentPayload.items)
    surveyPayload = survey
  } else {
    const localPageContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: `localPageCountry/${countrySlug}.json` },
    })
    const surveyContentPromise = apolloClient.query({
      query: getS3Content,
      variables: { key: 'survey/3.json' },
    })

    const [contentPayload, survey] = await Promise.all([
      localPageContentPromise,
      surveyContentPromise,
    ])

    locationPayload =
      R.head(R.pathOr([], ['data', 's3Content', 'items'], contentPayload)) ||
      null
    config = R.head(
      R.pathOr([], ['data', 's3Content', 'items'], contentPayload)
    )
    surveyPayload = survey.data.s3Content
  }

  const locationDetails = locationPayload && {
    country: {
      name: locationPayload?.fields?.country,
      slug: countrySlug,
    },
  }

  return { config, surveyPayload, locationDetails, stateRedirect: null }
}

export async function getStaticProps({ params: { cityOrCountrySlug } }) {
  const env = process.env.APP_ENV

  const localPageType =
    cityOrCountrySlug.length > 2
      ? LOCAL_PAGE_TYPE.CITY
      : LOCAL_PAGE_TYPE.COUNTRY

  const { config, surveyPayload, locationDetails, stateRedirect } =
    localPageType === LOCAL_PAGE_TYPE.CITY
      ? await cityStaticProps(env, cityOrCountrySlug)
      : await countryStaticProps(env, cityOrCountrySlug)

  if (!locationDetails) {
    return {
      redirect: {
        destination: `/local/us/personal-trainers`,
        permanent: true,
      },
    }
  }

  if (stateRedirect) {
    return {
      redirect: {
        destination: `/local/${stateRedirect}/personal-trainers`,
        permanent: true,
      },
    }
  }

  return {
    props: {
      statusCode: 200,
      fields: config.fields,
      survey: surveyPayload.items[0],
      locationDetails,
      localPageType,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths() {
  const { data } = await apolloClient.query({
    query: getCountryCitySlugs,
  })

  const paths = data.countryCitySlugs.map(
    slug => `/local/${slug}/personal-trainers`
  )

  return {
    paths,
    fallback: 'blocking',
  }
}

export default LocalPage
